import { BEARER_PREFIX, DEVICE_ID_HEADER, isDeviceId } from '@tarot/shared';
import { isToss } from './platform';

/**
 * 미니앱용 신원 보관 — 세션 토큰과 기기 식별값.
 *
 * 웹(tarotwith.com)은 이 파일을 쓰지 않는다. 쿠키가 그대로 동작하고, 세션을
 * JS가 읽을 수 없는 HttpOnly 쿠키에 두는 편이 더 안전하기 때문이다.
 *
 * 토스 미니앱은 토스가 호스팅하는 오리진에서 돌아 우리 쿠키가 서드파티가 되고
 * iOS가 이를 차단한다. 그 표면에서만 값을 localStorage에 두고 헤더로 보낸다
 * (doc/apps-in-toss-setup.md STEP 3).
 */

const TOKEN_KEY = 'tw_token';
const DEVICE_KEY = 'tw_did';
/** 기기 식별값 길이 — 서버의 형식 검사(shared `isDeviceId`)를 통과해야 한다 */
const DEVICE_ID_LEN = 24;
const ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/**
 * localStorage는 시크릿 모드·저장소 차단에서 **접근 자체가 예외를 던진다.**
 * 신원을 못 읽는 건 "비회원으로 시작"이면 충분한 상황이라, 실패는 전부 삼킨다.
 */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 저장 못 해도 이번 세션은 돌아간다 */
  }
}

/**
 * 이 기기의 식별값. 없으면 만들어 저장한다 (무작위 문자열 — 개인정보 아님).
 *
 * ⚠️ **메모리에도 들고 있어야 한다.** localStorage가 막힌 환경(시크릿 모드 등)에서는
 * 저장이 조용히 실패하는데, 그때 호출마다 새로 만들면 매 요청이 다른 기기로 보여
 * 하루 이용 횟수가 아예 쌓이지 않는다 — 제한이 무력화되는 쪽이다.
 */
let memoDeviceId: string | null = null;

export function deviceId(): string {
  if (memoDeviceId) return memoDeviceId;
  const saved = read(DEVICE_KEY);
  if (saved && isDeviceId(saved)) return (memoDeviceId = saved);
  const bytes = crypto.getRandomValues(new Uint8Array(DEVICE_ID_LEN));
  const fresh = Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join('');
  write(DEVICE_KEY, fresh);
  return (memoDeviceId = fresh);
}

export const sessionToken = (): string | null => read(TOKEN_KEY);

export function setSessionToken(token: string): void {
  write(TOKEN_KEY, token);
}

/** 탈퇴처럼 이 기기의 흔적을 지울 때 — 메모리에 든 값까지 버린다 */
export function clearIdentity(): void {
  memoDeviceId = null;
  clearSessionToken();
}

export function clearSessionToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* 지우지 못해도 서버 토큰은 만료된다 */
  }
}

/**
 * 이 요청에 실을 신원 헤더.
 *
 * **웹에서는 빈 객체**를 돌려준다 — 쿠키가 이미 하는 일을 헤더로 중복해 보낼 이유가 없고,
 * 웹 경로의 동작을 건드리지 않는 편이 안전하다.
 */
export function identityHeaders(): Record<string, string> {
  if (!isToss) return {};
  const headers: Record<string, string> = { [DEVICE_ID_HEADER]: deviceId() };
  const token = sessionToken();
  if (token) headers.Authorization = `${BEARER_PREFIX}${token}`;
  return headers;
}
